import {
	Badge,
	Box,
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Popover,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ArrowBack, FilterList, MoreHoriz, Search } from "@mui/icons-material";
import { PortalTooltip } from "@gui/widgets/PortalTooltip";
import { BusyButton } from "@gui/widgets/BusyButton";
import { BusyIcon } from "./BusyIcon";
import { PILL_RADIUS } from "@gui/providers/shape";
import type {
	PageAction,
	PageBack,
	PageFilters,
	PageSearch,
	PageTopToolbarConfig,
	PageViewAction,
} from "./PageLayout.types";
import { usePageTopToolbarViewModel, type PageTopToolbarViewModel } from "./PageTopToolbar.vm";
import { FilterChipRow, FilterGroups } from "./FilterChips";
import { FilterSheet } from "./FilterSheet";
import { BusyAnnouncement } from "./BusyAnnouncement";

/**
 * Every control in the top toolbar is this tall: a normal-size MUI button's height (14px label at
 * 1.75 line height, plus 6px padding top and bottom). The buttons keep their natural size and
 * padding; everything else is brought to them.
 */
const TOOLBAR_CONTROL_HEIGHT = 36.5;
const CONTROL_HEIGHT_SX = { height: TOOLBAR_CONTROL_HEIGHT } as const;
/**
 * Brings what a page puts in the toolbar (`custom` included) to the buttons' height, so no page has
 * to size its own controls: small text fields and pickers (40px by default) shrink, chips (24 or
 * 32px) and toggle groups grow, and a button a page sized itself is put back. Scoped to the toolbar; a field with a helper text or chips that
 * wrap would need its own sizing.
 */
export const TOOLBAR_CONTROLS_SX = {
	"& .MuiButton-root": CONTROL_HEIGHT_SX,
	"& .MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)": CONTROL_HEIGHT_SX,
	"& .MuiInputBase-sizeSmall:not(.MuiInputBase-multiline) .MuiInputBase-input": { py: 0 },
	"& .MuiChip-root": { ...CONTROL_HEIGHT_SX, borderRadius: PILL_RADIUS },
	"& .MuiToggleButtonGroup-root .MuiToggleButton-root": CONTROL_HEIGHT_SX,
	// An icon button a page puts in the row itself (the dashboard's saved-view ⋮), squared to the row's height. Only
	// direct children: the icon buttons inside a field (an Autocomplete's clear and open) keep their own size.
	"& > .MuiIconButton-root": { ...CONTROL_HEIGHT_SX, width: TOOLBAR_CONTROL_HEIGHT },
} as const;

/** A wrapping row. `useFlexGap` spaces with `gap`, so a wrapped line starts flush, not 8px in. */
const WRAP_ROW_SX = { flexWrap: "wrap" } as const;

/** True when the config would draw nothing, so the layout can drop the top toolbar row entirely. */
export function isToolbarEmpty(config: PageTopToolbarConfig): boolean {
	return (
		!config.back &&
		!config.search &&
		!config.filters?.groups.length &&
		!config.custom &&
		!config.customEnd &&
		!config.endActions?.length &&
		!config.actions?.length &&
		!config.primaryAction
	);
}

/** In a single row, search gives up width before anything else, down to a usable minimum. */
const SINGLE_ROW_SEARCH_SX = { flex: "1 1 320px", minWidth: 120 } as const;

function SearchField(props: {
	search: PageSearch;
	fullWidth: boolean;
	vm: PageTopToolbarViewModel;
	singleRow?: boolean;
}) {
	return (
		<TextField
			size="small"
			value={props.vm.searchValue}
			onChange={(event) => props.vm.editSearch(event.target.value)}
			placeholder={props.search.placeholder}
			fullWidth={props.fullWidth}
			sx={
				props.singleRow
					? { ...SINGLE_ROW_SEARCH_SX, maxWidth: props.fullWidth || props.search.fill ? undefined : 320 }
					: props.fullWidth
						? undefined
						: { width: 320, maxWidth: "100%" }
			}
			slotProps={{
				htmlInput: { "aria-label": props.search.placeholder },
				input: {
					startAdornment: (
						<InputAdornment position="start">
							<Search fontSize="small" />
						</InputAdornment>
					),
				},
			}}
		/>
	);
}

/** Takes the row's free width from a zero basis, so a long title truncates instead of wrapping the actions under it. */
const BACK_SX = { flex: "1 1 0", minWidth: 0 } as const;

function BackTitle(props: { back: PageBack }) {
	return (
		<Stack direction="row" alignItems="center" spacing={0.5} sx={BACK_SX}>
			<PortalTooltip title={props.back.label}>
				<IconButton edge="start" aria-label={props.back.label} onClick={props.back.onClick}>
					<ArrowBack />
				</IconButton>
			</PortalTooltip>
			<Box sx={{ minWidth: 0 }}>
				<Typography variant="subtitle1" component="h2" noWrap title={props.back.title} sx={{ lineHeight: 1.3 }}>
					{props.back.title}
				</Typography>
				{props.back.subtitle && (
					<Typography variant="caption" color="text.secondary" component="div" noWrap>
						{props.back.subtitle}
					</Typography>
				)}
			</Box>
		</Stack>
	);
}

function FiltersButton(props: { vm: PageTopToolbarViewModel }) {
	const count = props.vm.activeFilterCount;
	return (
		// The count sits on the button's corner, not on the icon, where it crowded the label. It is
		// already in the button's name, so the badge itself is hidden from assistive tech.
		<Badge badgeContent={count} color="primary" slotProps={{ badge: { "aria-hidden": true } }}>
			<Button
				variant="outlined"
				aria-label={count > 0 ? `Filters (${count} active)` : "Filters"}
				aria-haspopup="dialog"
				aria-expanded={props.vm.filterPopoverOpen || props.vm.sheetOpen}
				onClick={(event) => props.vm.openFilters(event.currentTarget)}
				startIcon={<FilterList />}
			>
				Filters
			</Button>
		</Badge>
	);
}

function FiltersControl(props: { filters: PageFilters; vm: PageTopToolbarViewModel }) {
	// One or two quick filters stay in the top toolbar as chips at every width.
	if (props.filters.inline) {
		return (
			<Stack direction="row" spacing={1} useFlexGap sx={WRAP_ROW_SX}>
				{props.filters.groups.map((group) => (
					<FilterChipRow
						key={group.id}
						group={group}
						selection={props.filters.selected}
						onToggle={props.vm.toggleApplied}
					/>
				))}
			</Stack>
		);
	}
	return (
		<>
			<FiltersButton vm={props.vm} />
			<Popover
				open={props.vm.filterPopoverOpen}
				anchorEl={props.vm.filterAnchor}
				onClose={props.vm.closeFilterPopover}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{
					paper: {
						role: "dialog",
						"aria-modal": true,
						"aria-label": "Filters",
						sx: { p: 2, width: 360, maxWidth: "90vw" },
					},
				}}
			>
				<FilterGroups
					groups={props.filters.groups}
					selection={props.filters.selected}
					onToggle={props.vm.toggleApplied}
				/>
				<Box sx={{ mt: 1.5, textAlign: "right" }}>
					<Button variant="text" size="small" onClick={props.vm.clearApplied} disabled={!props.vm.canClearFilters}>
						Clear All
					</Button>
				</Box>
			</Popover>
			<FilterSheet
				open={props.vm.sheetOpen}
				onClose={props.vm.closeSheet}
				groups={props.filters.groups}
				draft={props.vm.draft}
				onToggle={props.vm.toggleDraft}
				onClear={props.vm.clearDraft}
				onApply={props.vm.applyDraft}
			/>
		</>
	);
}

/** A menu item's icon slot; the spinner sits in it without shifting the label. */
const busySpinner = <CircularProgress size={16} color="inherit" />;

const actionKey = (action: PageViewAction, index: number) => action.id ?? `${index}-${action.label}`;

/** The one menu a menu action opens; a page has one top toolbar. */
const ACTION_MENU_ID = "page-toolbar-action-menu";
type MenuAria = { "aria-haspopup": "menu"; "aria-expanded": boolean; "aria-controls"?: string };
/** A menu action's button says it opens a menu, and whether its menu is the one open. */
const menuAriaOf = (action: PageViewAction, vm: PageTopToolbarViewModel): MenuAria | undefined => {
	if (!("menu" in action)) return undefined;
	const open = vm.isMenuOpen(action);
	return { "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? ACTION_MENU_ID : undefined };
};

function ActionButton(props: {
	action: PageViewAction;
	variant: "outlined" | "contained";
	onPress: (anchor: HTMLElement) => void;
	menuAria?: MenuAria;
}) {
	return (
		<BusyButton
			variant={props.variant}
			startIcon={props.action.icon}
			{...props.menuAria}
			onClick={(event) => props.onPress(event.currentTarget)}
			disabled={props.action.disabled}
			busy={props.action.busy}
			busyLabel={props.action.busyLabel}
		>
			{props.action.label}
		</BusyButton>
	);
}

/** The ⋯ button and icon-only actions: square, at the toolbar's height. */
const ICON_BUTTON_SX = { width: TOOLBAR_CONTROL_HEIGHT, ...CONTROL_HEIGHT_SX } as const;
/** An `iconOnly` primary: filled, with the 4px corners of a contained button, so it still reads as the primary. */
const PRIMARY_ICON_BUTTON_SX = {
	...ICON_BUTTON_SX,
	borderRadius: 1,
	bgcolor: "primary.main",
	color: "primary.contrastText",
	"&:hover": { bgcolor: "primary.dark" },
	"&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
} as const;

/**
 * An `iconOnly` action; also exported for a page that places one in `custom` (Skips' Reload, beside
 * its lookup box). Busy the way a BusyButton is: a spinner in place of the icon, `aria-busy`,
 * and clicks ignored while it keeps focus (`aria-disabled`, not `disabled`).
 */
export function ToolbarIconAction(props: { action: PageAction }) {
	return <IconAction action={props.action} onPress={props.action.onClick} />;
}

function IconAction(props: {
	action: PageViewAction;
	onPress: (anchor: HTMLElement) => void;
	primary?: boolean;
	menuAria?: MenuAria;
}) {
	const action = props.action;
	const busy = action.busy ?? false;
	return (
		<>
			<PortalTooltip title={action.label}>
				<IconButton
					sx={props.primary ? PRIMARY_ICON_BUTTON_SX : ICON_BUTTON_SX}
					aria-label={action.label}
					{...props.menuAria}
					onClick={busy ? undefined : (event) => props.onPress(event.currentTarget)}
					disabled={!busy && action.disabled}
					aria-disabled={busy || undefined}
					aria-busy={busy || undefined}
				>
					{busy ? <BusyIcon action={action} size={20} /> : action.icon}
				</IconButton>
			</PortalTooltip>
			<BusyAnnouncement action={action} />
		</>
	);
}

function OverflowMenu(props: { actions: readonly PageViewAction[]; vm: PageTopToolbarViewModel }) {
	// A busy action is inside a closed menu, so the ⋯ button itself shows that something is running.
	const busyAction = props.actions.find((action) => action.busy);
	const anyBusy = busyAction !== undefined;
	return (
		<>
			<PortalTooltip title="More actions">
				<IconButton
					size="small"
					sx={ICON_BUTTON_SX}
					aria-label="More actions"
					aria-haspopup="menu"
					aria-expanded={props.vm.overflowOpen}
					aria-busy={anyBusy || undefined}
					onClick={(event) => props.vm.openOverflow(event.currentTarget)}
				>
					{busyAction ? <BusyIcon action={busyAction} size={18} /> : <MoreHoriz />}
				</IconButton>
			</PortalTooltip>
			{props.actions.map((action, index) => (
				<BusyAnnouncement key={actionKey(action, index)} action={action} />
			))}
			<Menu anchorEl={props.vm.overflowAnchor} open={props.vm.overflowOpen} onClose={props.vm.closeOverflow}>
				{props.actions.map((action, index) => (
					<MenuItem
						key={actionKey(action, index)}
						disabled={action.disabled || action.busy}
						aria-busy={action.busy || undefined}
						onClick={() => props.vm.runOverflowAction(action)}
					>
						<ListItemIcon>{action.busy ? busySpinner : action.icon}</ListItemIcon>
						<ListItemText>{action.label}</ListItemText>
					</MenuItem>
				))}
			</Menu>
		</>
	);
}

/**
 * The top toolbar's fixed anatomy, rendered per breakpoint from data. Desktop: one row of search,
 * filters, the custom slot, then secondary actions as buttons, the customEnd slot and the primary
 * action rightmost.
 * Phone: search on a full-width row of its own, then filters, custom and a ⋯ menu holding the
 * secondary actions (a lone one stays a button). The commit group (endActions, customEnd, primary) normally reaches a phone's top
 * toolbar only with `phonePlacement: "top"`: PageLayout otherwise moves it to the bottom of the screen.
 */
export function PageTopToolbar(props: { config: PageTopToolbarConfig }) {
	const vm = usePageTopToolbarViewModel(props.config);
	const config = props.config;
	const actions = config.actions ?? [];
	const endActions = config.endActions ?? [];
	const secondary = [...actions, ...endActions];
	const hasFilters = Boolean(config.filters?.groups.length);
	const hasControls =
		hasFilters ||
		Boolean(
			config.back || config.custom || config.customEnd || actions.length || endActions.length || config.primaryAction,
		);

	const controls = (
		<>
			{config.back && <BackTitle back={config.back} />}
			{config.filters && hasFilters && <FiltersControl filters={config.filters} vm={vm} />}
			{config.custom}
			{/* The back title or a filling search already takes the free width. The spacer is a flex item even at zero
			    width, so it would add a second gap before the actions. */}
			{!config.back && !(config.singleRow && config.search?.fill) && <Box sx={{ flex: 1 }} />}
			{/* A phone folds secondary actions into ⋯, unless there is only one: a menu of one item is
			    just an extra tap (GeoFile Converter's Clear). */}
			{vm.isMobile && secondary.length > 1 ? (
				<OverflowMenu actions={secondary} vm={vm} />
			) : (
				secondary.map((action, index) =>
					action.iconOnly ? (
						<IconAction
							key={actionKey(action, index)}
							action={action}
							onPress={(anchor) => vm.activate(action, anchor)}
							menuAria={menuAriaOf(action, vm)}
						/>
					) : (
						<ActionButton
							key={actionKey(action, index)}
							action={action}
							variant="outlined"
							onPress={(anchor) => vm.activate(action, anchor)}
							menuAria={menuAriaOf(action, vm)}
						/>
					),
				)
			)}
			{config.customEnd}
			{config.primaryAction &&
				(config.primaryAction.iconOnly ? (
					<IconAction action={config.primaryAction} onPress={config.primaryAction.onClick} primary />
				) : (
					<ActionButton action={config.primaryAction} variant="contained" onPress={config.primaryAction.onClick} />
				))}
			<Menu
				slotProps={{ list: { id: ACTION_MENU_ID } }}
				anchorEl={vm.actionMenu?.anchor}
				open={vm.actionMenu !== null}
				onClose={vm.closeActionMenu}
			>
				{vm.actionMenu?.items.map((item, index) => (
					<MenuItem key={item.id ?? `${index}-${item.label}`} onClick={() => vm.runActionMenuItem(item)}>
						{item.label}
					</MenuItem>
				))}
			</Menu>
		</>
	);

	if (config.singleRow) {
		return (
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				alignItems="center"
				data-testid="page-top-toolbar"
				sx={{ flexWrap: "nowrap", ...TOOLBAR_CONTROLS_SX }}
			>
				{config.search && <SearchField search={config.search} fullWidth={vm.isMobile} vm={vm} singleRow />}
				{controls}
			</Stack>
		);
	}
	if (vm.isMobile) {
		return (
			<Stack spacing={1} data-testid="page-top-toolbar" sx={TOOLBAR_CONTROLS_SX}>
				{config.search && <SearchField search={config.search} fullWidth vm={vm} />}
				{hasControls && (
					<Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={WRAP_ROW_SX}>
						{controls}
					</Stack>
				)}
			</Stack>
		);
	}
	return (
		<Stack
			direction="row"
			spacing={1}
			useFlexGap
			alignItems="center"
			data-testid="page-top-toolbar"
			sx={{ ...WRAP_ROW_SX, ...TOOLBAR_CONTROLS_SX }}
		>
			{config.search && <SearchField search={config.search} fullWidth={false} vm={vm} />}
			{controls}
		</Stack>
	);
}
